import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import CardLegalText from "../CardLegalText";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import React from "react";
import RenderImage from "../RenderImage";
import CardSvodsSection from "./CardSvodsSection";
import OfferRibbon from "../OfferRibbon";
import PricingSection from "../PricingSection";
import CompareBox from "../CompareBox";
import { EqualHeightElement } from "react-equal-height/clean";
import { BackCardProps } from "../../../flip-cards.props";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import FusionTheme from "@directv-digital/fusion.themes.fusion";

const FOCUS_RING_SX = {
  "& a:focus-visible, & button:focus-visible": {
    outline: `2px solid ${colorPalette.blue[600]}`,
    outlineOffset: "2px",
    borderRadius: FusionTheme.fusion.shapes["shape-extra-small"]
  }
};

const getSubHeadingLinkSx = (linkColor?: string) => ({
  ...FOCUS_RING_SX,
  textAlign: "left",
  "& a, & button": {
    color: linkColor || "#FFFFFF",
    justifyContent: "flex-start",
    textAlign: "left"
  }
});

const styles = {
  container: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    borderRadius: "24px",
    overflow: "hidden",
    height: "100%"
  },
  inner: {
    background: "radial-gradient(circle farthest-corner at bottom right, #0062E3, #121212)",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    pb: {
      xs: "20px",
      sm: "16px",
      md: "26px"
    }
  },
  topSection: {
    pt: {
      xs: "36px",
      sm: "36px",
      md: "32px"
    },
    px: {
      xs: "16px",
      md: "24px"
    },
    mb: {
      xs: "8px",
      sm: "14px",
      md: "16px"
    },
    display: "flex",
    flexDirection: "column"
  },
  imageSection: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    alignItems: "center",
    justifyContent: "center"
  },
  bodySection: {
    p: "16px",
    display: "inline"
  },
  pricingWrapper: {
    pt: {
      xs: "16px",
      sm: "16px",
      md: "20px"
    },
    px: {
      xs: "16px",
      md: "24px"
    },
    mt: "auto"
  }
};

const BackCard = (props: BackCardProps) => {
  const { card, isFlippable, compareBox, cardId, isMobile } = props;

  // CMS may deliver a single image object instead of an array — normalize.
  const images = Array.isArray(card?.images) ? card.images : (card?.images ? [card.images] : []);

  try {
    return (
      <DtvBox sx={{
 ...styles.container,
...(card?.boxShadow && { boxShadow: card.boxShadow })
}}>
        {/* Card background inner */}
        <DtvBox
          sx={{
            ...styles.inner,
            ...(card?.background && { background: card.background })
          }}
        >
          <OfferRibbon cardType="back" isFlippable={isFlippable} offerRibbon={card?.offerRibbon} />

          {/* Title & Subheading */}
          <EqualHeightElement disable={isMobile} name="card-back-title-section">
            <DtvBox sx={styles.topSection}>
              {card?.title?.content && <DtvText {...card.title} component="h2" />}
              {(card?.subHeading?.content || card?.subHeading?.cta?.label) && (
                <DtvText {...card.subHeading} variant={card.subHeading.variant || "smalltext"} component="p" sx={getSubHeadingLinkSx(card?.subHeading?.color)} />
              )}
            </DtvBox>
          </EqualHeightElement>
          {/* SVODs section */}
          <CardSvodsSection svods={card?.svods || []} />

          {/* Images section */}
          <DtvBox data-testid="carousel-card-image-section" sx={styles.imageSection}>
            {images.map((image, idx) => (image ? <RenderImage key={image?.url?.default || idx} {...image} showReflection /> : null))}
          </DtvBox>

          {/* Body text */}
          {card?.bodyText?.content && (
            <DtvBox data-testid="carousel-card-body-section" sx={styles.bodySection}>
              <DtvText {...card.bodyText} content={`${card.bodyText.content}&nbsp;`} display="inline" />
              {card.bodyText.spanText?.content && (
                <DtvText {...card.bodyText.spanText} sx={{
 display: "inline",
textDecoration: "underline"
}} />
              )}
            </DtvBox>
          )}

          {/* Pricing section */}
          <DtvBox sx={styles.pricingWrapper}>
            <EqualHeightElement name="card-pricing-legal" disable={isMobile}>
              <DtvBox sx={{
 display: "flex",
flexDirection: "column"
}}>
                <PricingSection
                  cardType="back"
                  cta={card?.cta}
                  isMobile={isMobile}
                  mobileImagePosition={card?.mobileImagePosition}
                  priceText={card?.priceText}
                  strikeThroughPrice={card?.strikeThroughPrice}
                  cardName={card?.title?.content}
                />
                {card?.legalText?.content && (
                  <CardLegalText
                    legalText={card.legalText}
                    cardId={cardId}
                    side="back"
                    fallbackColor={card?.priceText?.color || "rgba(255, 255, 255, 0.85)"}
                  />
                )}
              </DtvBox>
            </EqualHeightElement>
          </DtvBox>
        </DtvBox>

        {/* Compare Box - outside inner to avoid card background */}
        {compareBox?.content && (
          <CompareBox cardId={cardId} cardType="back" compareBox={compareBox} cardName={card?.title?.content} />
        )}
      </DtvBox>
    );
  } catch (error) {
    return <ErrorDisplay component="BackCard" error={error} />;
  }
};

export default React.memo(BackCard);
